import { Directive, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SearchRegisteredUserQueryParams } from '@domains/admin/models/clerk-probate-http.models';
import {
  tap,
  catchError,
  finalize,
  of,
  debounceTime,
  switchMap,
  Subject,
} from 'rxjs';
import { UserSearchStateService } from '../services/user-search-state.service';
import { Toast } from '@shared/components/toast/interfaces';
import { ToastService } from '@shared/components/toast';
import { AdminService } from '@domains/admin/services/admin.service';

@Directive({
  selector: '[appHandleUserSearch]',
  host: {
    '(searchChange)': 'handleSearch($event)',
  },
})
export class HandleUserSearchDirective {
  public searchType = input.required<'firstName' | 'email'>();
  public isFirm = input.required<boolean>();

  private readonly _adminService = inject(AdminService);
  private readonly _userSearchStateService = inject(UserSearchStateService);
  private readonly _toast = inject(ToastService);

  private readonly _searchSubject = new Subject<string>();
  private readonly _defaultDebounceTime = 300;
  private readonly _errorToast: Toast = {
    type: 'error',
    message: 'An error occurred while searching for users. Please try again.',
  };

  protected handleSearch(event: Event): void {
    const searchValue = event as unknown as string;
    if (searchValue.length < 1) {
      if (this.searchType() === 'firstName') {
        this._userSearchStateService.setSearchedUsersByName([]);
      } else {
        this._userSearchStateService.setSearchedUsersByEmail([]);
      }
      return;
    }

    this._searchSubject.next(searchValue);
  }

  constructor() {
    this._searchSubject
      .pipe(
        debounceTime(this._defaultDebounceTime),
        tap(() => this._userSearchStateService.setIsLoading(true)),
        switchMap((searchValue) => {
          const params: SearchRegisteredUserQueryParams = {
            [this.searchType()]: searchValue,
            isFirm: this.isFirm(),
          };
          return this._adminService.getRegisteredUsers(params).pipe(
            catchError(() => {
              this._toast.show(this._errorToast);
              return of([]);
            }),
            finalize(() => this._userSearchStateService.setIsLoading(false)),
          );
        }),
        tap((options) => {
          if (this.searchType() === 'firstName') {
            this._userSearchStateService.setSearchedUsersByName(options);
          } else {
            this._userSearchStateService.setSearchedUsersByEmail(options);
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }
}
